// [2026-09-02] ADDED — the CUSTOMER shell, signed in through the InCruiter account (SSO).
//
// WHY THIS EXISTS ALONGSIDE App.jsx, NOT INSTEAD OF IT.
// App.jsx is a cross-repo contract: proctoring-frontend-admin imports it (and NavGroup/NavItem/api)
// and renders the STAFF dashboard from it, which still uses the email/password door. Ripping the
// login out of App.jsx would change the admin app under it. So the customer app gets its OWN thin
// shell here — same sidebar primitives, same toast host — and App.jsx is left exactly as the admin
// app expects it.
//
// WHAT IT DOES: verify the account session once (GET /me), then show the three screens the deployed
// proctoring-sdk-backend actually feeds a customer — Overview, API keys, Credits. It does NOT render
// the nine rich analytics/recording/candidate screens: those need client-scoped endpoints the SDK
// backend does not expose, and showing them fed by nothing would be worse than not showing them.

import { useCallback, useEffect, useState } from 'react'
// [2026-09-11] CHANGED — getPlatformToken added, for the Bearer on the logout call.
// OLD: import { platform, setPlatformToken } from './api.js'
import { getPlatformToken, platform, setPlatformToken } from './api.js'
// The sidebar primitives are exported from App.jsx precisely so a second shell looks identical.
// [2026-09-04] NOTE — no longer rendered here (see the shell change below), but the import is KEPT.
// It is what makes the commented-out sidebar at the bottom of this file a one-edit restore, and
// removing it would also remove the only in-repo reminder that App.jsx exports these for the admin
// app (src/index.js:51 calls that a cross-repo contract).
// eslint-disable-next-line no-unused-vars
import { NavGroup, NavItem } from './App.jsx'
import { RangePicker, Skeleton, ToastHost, toast } from './ui.jsx'
// [2026-09-04] ADDED — the revamped shell and landing screen, built to the IncProctor Figma.
import PlatformShell from './components/platform/PlatformShell.jsx'
// [2026-09-07] ADDED — see roles.js. The SAME helper the shell uses to decide whether to draw the
// API Keys tab, so the tab and the deep link can never disagree about who is allowed in.
import { canManageKeys } from './roles.js'
// [2026-09-27] CHANGED — PfPageSpinner added: the start-up loader is the InCruiter spinner now.
// OLD: import { PfNotice } from './components/platform/ui.jsx'
import { PfNotice, PfPageSpinner } from './components/platform/ui.jsx'
// [2026-09-05] ADDED — the account app's host, derived from ours. See productLinks.js.
import { generateUrl } from './components/platform/productLinks.js'
import PlatformDashboard from './views/platform/PlatformDashboard.jsx'
import PlatformLogin from './components/auth/PlatformLogin.jsx'
import PlatformOverview from './views/platform/PlatformOverview.jsx'
import PlatformKeys from './views/platform/PlatformKeys.jsx'
import PlatformCredits from './views/platform/PlatformCredits.jsx'
// [2026-09-03] ADDED — team members, and the page an invitation link opens.
import PlatformMembers from './views/platform/PlatformMembers.jsx'
import AcceptInvite from './components/AcceptInvite.jsx'
// [2026-09-02] ADDED — the analytics, request-feed and report screens.
import PlatformAnalytics from './views/platform/PlatformAnalytics.jsx'
import PlatformRequests from './views/platform/PlatformRequests.jsx'
import PlatformReports from './views/platform/PlatformReports.jsx'
// [2026-09-06] ADDED — the analysis API's submitted jobs, a second tab on the Reports screen.
import PlatformVideoJobs from './views/platform/PlatformVideoJobs.jsx'
// [2026-09-06] ADDED — the video-processing call log, a second tab on the API Requests screen.
import PlatformVideoRequests from './views/platform/PlatformVideoRequests.jsx'

// [2026-09-04] CHANGED — the titles and blurbs are now the IncProctor Figma's wording.
//
// THE VIEW KEYS ARE UNCHANGED, and that is the point: `?view=reports` links already in circulation
// (the "check your proctoring report" email, anything a customer bookmarked) keep working, and the
// e2e suites keep navigating by the same ids. This is a relabelling, not a re-route.
//
// OLD:
//   overview:  { title: 'Overview', blurb: 'Your company, balance and traffic at a glance.' },
//   analytics: { title: 'Usage & analytics',
//                blurb: 'Volume, latency, failures and what your candidates did.' },
//   reports:   { title: 'Proctoring reports',
//                blurb: 'Every candidate session, with its full event timeline.' },
//   requests:  { title: 'API requests',
//                blurb: 'One row per API call — the drill-down for a specific failure.' },
//   keys:      { title: 'API keys', blurb: 'Create, rotate and revoke your credentials.' },
//   credits:   { title: 'Credits',
//                blurb: 'Balance and ledger — what you were granted and what you have used.' },
//   members:   { title: 'Team',
//                blurb: 'Who at your company can open this dashboard, and what they can do.' },
const VIEWS = {
  overview:  { title: 'Dashboard', blurb: "Welcome back! Here's your recruitment overview" },
  // [2026-09-04] CHANGED — title-cased to match the nav label now that these two are on the bar.
  // OLD: analytics: { title: 'Usage & analytics', … }
  //      credits:   { title: 'Credits', … }
  // [2026-09-07] REMOVED — the Usage & Analytics screen is off the product.
  //   analytics: { title: 'Usage & Analytics',
  //                blurb: 'Volume, latency, failures and what your candidates did.' },
  //
  // The VIEWS map is also what validates ?view= (see initialView below), so removing the entry is
  // what makes /?view=analytics fall back to the dashboard instead of rendering a titleless page.
  // Commenting the nav item alone would have left the screen reachable by URL.
  reports:   { title: 'Proctoring Reports',
               blurb: 'Manage and track all your Proctoring Reports' },
  requests:  { title: 'API Requests',
               blurb: 'Manage and track all your API Requests' },
  keys:      { title: 'API keys', blurb: 'Manage and track all your api keys' },
  credits:   { title: 'Credits & Billing',
               blurb: 'Balance and ledger — what you were granted and what you have used.' },
  // [2026-09-03] ADDED — the Team screen. It is the ONLY place member records can be created, and
  // the backend now refuses anyone without one, so without this the lock is a door with no handle.
  // [2026-09-04] CHANGED — OLD: { title: 'Team', blurb: 'Who at your company can open this
  // dashboard, and what they can do.' }
  //
  // The screen had TWO names — "Add Users" on the bar and "Team" as the heading — which reads as two
  // different places. One name now. The blurb still says the screen lists and manages people, so
  // "Add Users" naming the entry point does not overstate what it does.
  members:   { title: 'Add Users',
               blurb: 'Invite people, and see who at your company can already open this dashboard.' },
}

// THE TIME RANGE IS SHARED AND LIVES IN THE SHELL, for the reason App.jsx's header gives: one
// control applied to every panel. Per-screen ranges are how two charts end up silently showing
// different windows while sitting side by side.
//
// The tables and the key screen take no range — a report table paginates over everything, and a
// credential has no time window. Listing them here keeps that decision in ONE place instead of
// repeated as a condition at each render site.
// [2026-09-02] CHANGED — 'credits' removed from this list.
//
// OLD: const RANGELESS_VIEWS = ['keys', 'requests', 'reports', 'members']   (unchanged — see below)
//
// Nothing was removed: 'credits' was never IN the list. That was the bug. This shell has always
// passed `days` to PlatformCredits (below), but that screen ignored the prop, so the range picker
// rendered above the credits screen and moving it did nothing — a control that looks like it worked
// is worse than one that is absent. PlatformCredits now applies the window to its spend chart, so
// the picker belongs there and this list stays as it is.
//
// [2026-09-04] CHANGED — 'overview' added.
// OLD: const RANGELESS_VIEWS = ['keys', 'requests', 'reports']
//
// Not because the dashboard has no time window — it has the same one — but because the Figma puts
// that control ON the Traffic card, and PlatformDashboard renders it there (its RangeSelect drives
// this same `days` state). Leaving it in the page head as well would put TWO controls for one value
// on one screen: they would stay in sync, and a user would still have to work out that they are the
// same control. One value, one control — which is what the whole comment above is about.
// [2026-09-04] CHANGED AGAIN — 'members' added, for the reason the comment above already gives
// about 'credits'. This screen is rendered as a bare `<PlatformMembers />`: it is passed no `days`
// prop and reads none, so the picker sat above the Team table doing NOTHING. That predates this
// revamp; it only became obvious once the head was a wide bar with the control alone in it. A
// control that looks like it worked is worse than one that is absent.
const RANGELESS_VIEWS = ['keys', 'requests', 'reports', 'overview', 'members']

// [2026-09-02] ADDED — deep-link the opening screen with `?view=reports`, matching App.jsx:114.
//
// WHY A QUERY PARAM AND NOT A PATH, verbatim from the reasoning already proven in App.jsx: the view
// is held in state because the backend serves only "/" and "/static", so a path like /reports would
// 404 on reload or on a shared link. A query string leaves the path alone.
//
// This shell had no equivalent, which meant a customer arriving from a "check your proctoring
// report" email always landed on Overview and had to find the screen themselves. Unknown or absent
// value => 'overview', i.e. exactly the behaviour before this change.
function initialView() {
  try {
    const requested = new URLSearchParams(window.location.search).get('view')
    return requested && VIEWS[requested] ? requested : 'overview'
  } catch {
    // A malformed URL must never stop the dashboard from booting.
    return 'overview'
  }
}

// A minimal sign-out / brand icon set, reusing the same <svg> convention as App.jsx's NavItem.
const SignOutIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
       strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9" />
  </svg>
)

export default function PlatformApp() {
  // status: 'loading' | 'signedout' | 'off' | 'error' | 'ready'
  const [status, setStatus] = useState('loading')
  const [session, setSession] = useState(null)
  const [message, setMessage] = useState(null)
  // [2026-09-02] CHANGED — seeded from `?view=`. OLD: useState('overview').
  const [view, setView] = useState(initialView)
  const [nonce, setNonce] = useState(0)   // bumped to re-run the session check after a token paste
  // [2026-09-02] ADDED — the shared analytics window, in days.
  const [days, setDays] = useState(30)
  // [2026-09-02] ADDED — the endpoint the request feed is scoped to, set by clicking a row in the
  // analytics endpoint table. Held HERE rather than inside PlatformRequests because the click that
  // sets it happens on a different screen; a value owned by the screen being navigated TO cannot be
  // set by the screen being navigated FROM.
  const [drillEndpoint, setDrillEndpoint] = useState(null)
  // [2026-09-04] ADDED — the Figma puts "Create API Key +" in the HEADER, on every screen, and the
  // create dialog lives inside PlatformKeys. A boolean would not work: pressing the button twice in
  // a row, or pressing it while already on the keys screen after cancelling, must re-open the
  // dialog, and a flag that is already `true` produces no change for PlatformKeys to react to. A
  // counter always changes, so every press is a distinct event.
  const [createKeyNonce, setCreateKeyNonce] = useState(0)

  // Keep the URL honest as the user navigates, so the address bar is always a shareable link to the
  // screen actually on display. replaceState, not pushState: this app has no history handling, and
  // pushing would make Back walk through every sidebar click and then leave the page — the same
  // trap App.jsx avoids at line 598. Failure here must never break navigation.
  useEffect(() => {
    try {
      const url = new URL(window.location.href)
      url.searchParams.set('view', view)
      window.history.replaceState({}, '', url)
    } catch { /* non-standard URL — navigation still works, the address bar just will not update */ }
  }, [view])

  // Navigating away from the request feed by hand drops the endpoint scope. Without this, going
  // Analytics -> requests (filtered) -> Overview -> requests would silently re-apply a filter the
  // user set several clicks ago and has no reason to still expect.
  const goTo = useCallback((next) => {
    if (next !== 'requests') setDrillEndpoint(null)
    setView(next)
  }, [])

  // Clicking an endpoint row on Analytics opens the request feed already filtered to it.
  const drillIntoEndpoint = useCallback((endpoint) => {
    setDrillEndpoint(endpoint)
    setView('requests')
  }, [])

  // [2026-09-04] ADDED — the header's "Create API Key +". It navigates to the keys screen AND bumps
  // the nonce, in that order, so the dialog opens over the list it will add a row to rather than
  // over whatever screen the user happened to be on. Landing on the list also means the new key is
  // visible the moment the dialog closes.
  const startCreateKey = useCallback(() => {
    setDrillEndpoint(null)
    setView('keys')
    setCreateKeyNonce((n) => n + 1)
  }, [])

  // [2026-09-24] ADDED — the header's "Create API Key +" now only OPENS the API Keys screen; it no
  // longer pops the create dialog (asked for by Rujal, 2026-09-24: "I don't want the popup, I want
  // it to only direct me to the page"). The dialog is one click away there. The nonce is set to 0
  // so arriving by this button never re-opens a dialog a previous press left armed.
  // The Dashboard's "Create API keys" quick action still uses startCreateKey above and opens the
  // dialog — that button is labelled as an action, not a destination.
  const goToKeys = useCallback(() => {
    setDrillEndpoint(null)
    setCreateKeyNonce(0)
    setView('keys')
  }, [])

  // [2026-09-24] ADDED — disarm the create dialog whenever the user leaves the keys screen.
  //
  // THE BUG: createKeyNonce was never reset, and PlatformKeys opens its dialog whenever it MOUNTS
  // with a nonce above 0 (PlatformKeys.jsx, the openCreateNonce effect). So after one create press,
  // every later visit to API Keys from the nav remounted the screen and popped the dialog unasked.
  // Resetting on the way out keeps "one press, one dialog".
  useEffect(() => {
    if (view !== 'keys') setCreateKeyNonce(0)
  }, [view])

  useEffect(() => {
    let live = true
    setStatus('loading')
    platform.me()
      .then((data) => { if (live) { setSession(data); setStatus('ready') } })
      .catch((err) => {
        if (!live) return
        setMessage(err.message)
        // [2026-09-04] CHANGED — NOT_A_MEMBER is its own state, and that distinction prevents an
        // infinite redirect loop.
        //
        // OLD: everything that was not NOT_AUTHENTICATED / FEATURE_OFF fell into 'signedout'.
        //
        // The gate now sends a signed-out visitor straight to the InCruiter login. A person who IS
        // signed in but has not been granted proctoring gets 403 NOT_A_MEMBER — if that also landed
        // in 'signedout' they would be bounced to a login they are already through, come back, be
        // refused again, and bounce forever. They need a sentence, not a redirect.
        // [2026-09-08] ADDED — NO_PRODUCT_ACCESS joins NOT_A_MEMBER, and for the identical reason
        // spelled out just above.
        //
        // OLD:
        //   if (err.code === 'NOT_AUTHENTICATED') setStatus('signedout')
        //   else if (err.code === 'NOT_A_MEMBER') setStatus('noaccess')
        //   else if (err.code === 'FEATURE_OFF') setStatus('off')
        //   else setStatus('signedout')
        //
        // The backend now refuses a sub-user whose InCruiter ROLE has InC-Proctor switched off
        // (app/modules/me/route.py, THE INCRUITER ROLE GATE → 403 NO_PRODUCT_ACCESS). Without this
        // line that code falls into the `else` and becomes 'signedout' — which sends a person who
        // IS signed in to the InCruiter login, which returns them here, which refuses them again.
        // That is the exact infinite loop the 2026-09-04 note above describes; a new refusal code
        // walked straight back into it.
        //
        // 'noaccess' is the right state and needs nothing else: it renders `message` verbatim
        // (PlatformLogin.jsx:59-66), and the backend's message names the fix — ask your account
        // administrator to enable InC-Proctor for your role. It deliberately shows no sign-in
        // button, which is what stops the loop.
        if (err.code === 'NOT_AUTHENTICATED') setStatus('signedout')
        else if (err.code === 'NOT_A_MEMBER') setStatus('noaccess')
        else if (err.code === 'NO_PRODUCT_ACCESS') setStatus('noaccess')
        else if (err.code === 'FEATURE_OFF') setStatus('off')
        else setStatus('signedout')   // network/unknown: let them retry / paste a token
      })
  }, [nonce])

  const onToken = useCallback((token) => {
    setPlatformToken(token)
    setMessage(null)
    setNonce((n) => n + 1)
  }, [])

  // [2026-09-11] ADDED — end the InCruiter session, not just ours.
  //
  // Asked for by Rujal: "sign out is not signing out totally — it should take me to the login page,
  // the way the profile page does."
  //
  // WHAT WAS WRONG. signOut below cleared `proctor.platform_token` out of sessionStorage and set
  // the app to 'signedout'. That is the whole of it — the INCRUITER session was untouched. So the
  // person was signed out of this tab and still signed in everywhere else, and the signed-out
  // effect then bounced them to the account app, which saw a perfectly valid session and let them
  // straight back in. It looked like sign-out did nothing, because in every sense that matters to
  // the user it did nothing.
  //
  // THIS IS THE ACCOUNT APP'S OWN LOGOUT, not an invention: frontend-account
  // src/redux/saga/loginSaga.jsx:334-362 does `AUTH.patch('auth/logout')`, clears storage, and ends
  // with `window.location.replace(mainWebsite + '/employer/login')`. The two steps that matter to
  // somebody outside that app are the PATCH and the destination, and those are what this does.
  //
  // BEST EFFORT, AND DELIBERATELY SO. The redirect happens whether or not the PATCH succeeds —
  // which is not a shortcut, it is what the account app does too: its `catch` block clears and
  // redirects identically. The reasoning is that a failed logout call must never strand somebody on
  // a page they have asked to leave; being sent to the login screen with a session that outlived
  // the request is recoverable, and a dead button is not.
  //
  // ⚠️ VERIFY-BEFORE-PROD: whether the auth API sends CORS headers for this origin has NOT been
  // tested from a browser — only read out of the account app, which is same-origin with that API
  // and therefore proves nothing about ours. If it is blocked, the PATCH fails, `.catch` swallows
  // it, and the redirect still happens: the user reaches the login page and the local session is
  // gone, which is already better than today. If it IS blocked, the fix is a CORS entry for this
  // origin on user-auth-system, NOT a change here.
  const endIncruiterSession = useCallback(() => {
    const api = generateUrl('user_backend')
    const token = getPlatformToken()
    if (!api || !token) return Promise.resolve()
    return fetch(`${api}/auth/logout`, {
      method: 'PATCH',
      headers: { Authorization: `Bearer ${token}` },
      // The session cookie lives on .incruiter.com and this app is a subdomain of it, so it rides
      // along — the API identifies the session from the pair, exactly as it does for the account app.
      credentials: 'include',
      // keepalive so the request survives the navigation that follows on its heels. Without it the
      // browser is entitled to cancel an in-flight fetch when the document unloads, which would make
      // the logout succeed or fail depending on how fast the network was.
      keepalive: true,
    }).catch(() => { /* see the best-effort note above */ })
  }, [])

  const signOut = useCallback(() => {
    setPlatformToken(null)
    setSession(null)
    setView('overview')
    // [2026-09-02] ADDED — a drill target must not survive a sign-out. The next person to sign in
    // on this tab would otherwise open the request feed silently filtered to someone else's search.
    setDrillEndpoint(null)

    // [2026-09-11] CHANGED — leave for the InCruiter login instead of falling through to the
    // signed-out state.
    // OLD: setStatus('signedout')
    //
    // 'signedout' is still the state for a session that EXPIRED (onError below sets it, and the
    // effect under it bounces to the account app). That is the right behaviour there and the wrong
    // behaviour here: an expiry means "prove who you are again", and the account app can answer
    // that silently from a live cookie. A deliberate sign-out means "end it", so it must go to the
    // login page and the cookie must not survive to answer anything.
    const site = generateUrl('website')
    const isLocal = /^(localhost|127\.0\.0\.1)$/.test(window.location.hostname)
    if (!site || isLocal) {
      // LOCALHOST KEEPS THE OLD BEHAVIOUR, for the reason the redirect effect below already gives:
      // there is no .incruiter.com cookie on a dev server, so there is nothing to end, and sending a
      // developer to the real login page would take them off their own dev server with no way back.
      // The paste-a-token card is the local flow and it must survive.
      setStatus('signedout')
      return
    }
    // The one-shot redirect guard is cleared so that whoever signs in next on this tab gets the
    // normal signed-out bounce if THEIR session later expires. Leaving it set would suppress that
    // for the life of the tab.
    try { sessionStorage.removeItem('proctor.redirected') } catch { /* private browsing */ }

    endIncruiterSession().finally(() => {
      // replace(), not href — the dashboard must not sit in history for Back to return to, or the
      // person lands on a page that immediately redirects again. Same reasoning as the effect below.
      window.location.replace(`${site}/employer/login`)
    })
  }, [endIncruiterSession])

  // A 401 raised by any screen mid-use means the session died — drop back to the gate once.
  const onError = useCallback((err) => {
    if (err?.code === 'NOT_AUTHENTICATED') {
      signOut()
      toast('Your session expired. Please sign in again.', true)
    }
  }, [signOut])

  // [2026-09-04] ADDED — a signed-out visitor goes STRAIGHT to the InCruiter login.
  //
  // Pasting this URL to someone must not show them a branded page with a button; every other
  // InCruiter product bounces an unauthenticated visitor to the one login. This makes proctoring
  // behave the same, so the link is worthless on its own.
  //
  // THREE GUARDS, each preventing a specific way an auto-redirect goes wrong:
  //
  //   1. ONCE PER TAB. If the account portal sends them back still signed out — cookie blocked,
  //      third-party cookie policy, a session that dies immediately — an unguarded redirect is an
  //      infinite loop between two domains, which looks like the product is down. After one
  //      attempt the sign-in card is shown instead, so the second failure is visible and
  //      explicable rather than a spinning tab.
  //   2. NOT ON LOCALHOST. The .incruiter.com cookie is never sent to localhost, so a developer
  //      would be redirected away from their dev server on every load with no way back. The card's
  //      paste-a-token path is the local flow and must survive.
  //   3. ONLY FOR 'signedout'. 'noaccess' and 'off' render a message — see the state split above.
  //
  // replace(), not href: the dashboard URL must not sit in history for Back to return to, or the
  // person lands on a page that immediately redirects again.
  useEffect(() => {
    if (status !== 'signedout') return
    // [2026-09-05] CHANGED — derived from our own hostname, not from VITE_ACCOUNT_URL.
    //
    // OLD: const accountUrl = import.meta.env?.VITE_ACCOUNT_URL
    //
    // THIS REDIRECT HAS NEVER RUN IN THE DEPLOYED DEV BUILD. VITE_ACCOUNT_URL is empty there
    // (verified 2026-09-05 against the live bundle — see productLinks.js), so `!accountUrl` was
    // true on every load and the effect returned before doing anything. The behaviour this block
    // describes at length — a signed-out visitor going straight to the InCruiter login, the way
    // every other InCruiter product behaves — simply did not happen, and nothing said so: no error,
    // no log, just a sign-in card where a redirect was intended. A feature guarded by an unset
    // variable fails silently and indefinitely, which is why the value now comes from somewhere
    // that cannot be unset.
    //
    // The two guards below are UNCHANGED and both still matter: localhost must never be redirected
    // away from, and the once-per-tab flag is what stops a still-signed-out return trip becoming a
    // loop between two domains.
    const accountUrl = generateUrl('account')
    const isLocal = /^(localhost|127\.0\.0\.1)$/.test(window.location.hostname)
    if (!accountUrl || isLocal) return
    try {
      if (sessionStorage.getItem('proctor.redirected') === '1') return
      sessionStorage.setItem('proctor.redirected', '1')
    } catch { /* private browsing: fall through and redirect once */ }
    window.location.replace(accountUrl)
  }, [status])

  // Clear the one-shot guard as soon as a session works, so a genuine expiry later in the day
  // redirects again instead of being permanently suppressed for this tab.
  useEffect(() => {
    if (status === 'ready') {
      try { sessionStorage.removeItem('proctor.redirected') } catch { /* nothing to clear */ }
    }
  }, [status])

  // [2026-09-27] CHANGED — while the session is being checked, show the spinner every other
  // InCruiter app shows (see PfPageSpinner in components/platform/ui.jsx), not a grey block.
  // Asked for by Rujal: "currently I am seeing a square loading thing, but I want a spinner".
  // OLD:
  // if (status === 'loading') {
  //   return <div className="login-wrap"><Skeleton height={120} style={{ width: 380 }} /></div>
  // }
  if (status === 'loading') {
    return <PfPageSpinner />
  }
  // Signed in, but this company has not granted them proctoring. A dead end by design — the fix is
  // an administrator inviting them, not anything they can do here.
  if (status === 'noaccess') {
    return (
      <>
        <PlatformLogin mode="noaccess" message={message} onToken={onToken} />
        <ToastHost />
      </>
    )
  }
  if (status === 'signedout' || status === 'off') {
    return (
      <>
        <PlatformLogin mode={status === 'off' ? 'off' : 'signin'} message={message} onToken={onToken} />
        <ToastHost />
      </>
    )
  }

  const meta = VIEWS[view] || VIEWS.overview

  // [2026-09-04] CHANGED — the shell is now PlatformShell (product rail + top navigation), built to
  // the IncProctor Figma. The 236px sidebar this file used to draw inline is preserved verbatim at
  // the bottom of this file; restoring it is one edit and no other file has to move.
  //
  // WHAT DID NOT CHANGE, DELIBERATELY:
  //   * every `view` id, so `?view=…` deep links and the e2e navigation still resolve;
  //   * goTo / drillIntoEndpoint / onError / signOut — the routing and session behaviour is the
  //     same code it was, only the chrome around it is new;
  //   * the `key={drillEndpoint || 'all'}` remount on the request feed, for the reason below;
  //   * App.jsx, NavGroup and NavItem, which proctoring-frontend-admin compiles against.
  //
  // 'overview' now renders PlatformDashboard (the Figma's merged landing page) instead of
  // PlatformOverview. PlatformOverview is NOT deleted and still compiles — swap the two names on
  // the line below to go back.
  return (
    <PlatformShell
      view={view}
      setView={goTo}
      session={session}
      onSignOut={signOut}
      // [2026-09-24] CHANGED — the header button only navigates now; see goToKeys.
      // OLD: onCreateKey={startCreateKey}
      onCreateKey={goToKeys}
      title={meta.title}
      blurb={meta.blurb}
      actions={!RANGELESS_VIEWS.includes(view)
        ? <RangePicker value={days} onChange={setDays} />
        : null}
    >
      {view === 'overview' && (
        <PlatformDashboard session={session} days={days} setDays={setDays} setView={goTo}
                           onError={onError} onDrillEndpoint={drillIntoEndpoint}
                           onCreateKey={startCreateKey} />
      )}
      {/* [2026-09-02] CHANGED — analytics can now drill into the request feed.
          OLD: {view === 'analytics' && <PlatformAnalytics days={days} onError={onError} />}
               {view === 'requests' && <PlatformRequests onError={onError} />} */}
      {/* [2026-09-06] CHANGED — `session` is now passed, for the API-surface credit tile.
          OLD: <PlatformAnalytics days={days} onError={onError} onDrillEndpoint={drillIntoEndpoint} />
          The shell already holds the session (it is fetched once for the whole app), so this is a
          prop rather than a second /me/session call from inside the screen. */}
      {/* [2026-09-07] REMOVED — the Usage & Analytics screen is off the product.
          `false &&` rather than a comment block: the JSX stays live code, so it is still linted and
          still fails the build if a prop it takes is renamed. Commented-out markup rots silently and
          is found broken on the day someone tries to restore it. Delete the two words to bring it
          back — and uncomment the VIEWS entry above and the nav item in PlatformShell.jsx. */}
      {false && view === 'analytics' && (
        <PlatformAnalytics session={session} days={days} onError={onError}
                           onDrillEndpoint={drillIntoEndpoint} />
      )}
      {/* [2026-09-06] CHANGED — the Reports screen now has two tabs.
          OLD: {view === 'reports' && <PlatformReports onError={onError} />}

          WHY A TAB AND NOT A SEVENTH NAV ITEM. Both tabs answer "what work has been done for my
          candidates" — one from the browser SDK (live sessions with an event timeline), one from
          the analysis API (recordings submitted for processing). They are two sources for one
          question, and the nav is already seven items. The tab keys are NOT in the URL, so
          `?view=reports` still lands on the sessions tab exactly as every existing link expects. */}
      {view === 'reports' && <ReportsTabs onError={onError} />}
      {view === 'members' && <PlatformMembers />}
      {/* The `key` is what makes the seeded filter work. DataTable reads `initialFilters` once, as
          initial state, so that clearing a filter is not undone by the next parent render; keying
          on the endpoint remounts the screen when the drill target changes, which is the only way
          a NEW seed reaches it. */}
      {/* [2026-09-06] CHANGED — the API Requests screen now has two tabs.
          OLD: <PlatformRequests key={drillEndpoint || 'all'} endpoint={drillEndpoint || undefined}
                                 onError={onError} />

          THE KEY MOVES TO THE HOST, and it must. It is what makes the seeded endpoint filter work
          at all — DataTable reads `initialFilters` once, as initial state, so only a remount
          delivers a NEW seed. Keying the inner table alone would remount the table but leave the
          host's tab state untouched, so drilling into an endpoint while the Video Processing tab
          was open would silently show the video feed instead of the endpoint just clicked.
          Remounting the host resets both together: the filtered feed, on the tab that has the
          filter. */}
      {view === 'requests' && (
        <ApiRequestTabs key={drillEndpoint || 'all'} endpoint={drillEndpoint || undefined}
                        onError={onError} />
      )}
      {/* [2026-09-07] CHANGED — the keys screen is not rendered for a viewer.
          OLD: {view === 'keys' && <PlatformKeys onError={onError} openCreateNonce={createKeyNonce} />}

          Hiding the nav item is not enough on its own. `?view=keys` is a real, shareable deep link
          (the URL is kept in sync on every navigation, just above), so a viewer sent one by a
          colleague — or one who was an admin yesterday and has a tab still open — would land on a
          screen whose every button 403s.

          A SENTENCE, NOT A REDIRECT. Bouncing them to the Dashboard would look like the link was
          broken, and they would send it again. This tells them what happened and who can fix it,
          which is the same shape the NOT_A_MEMBER gate uses above for the same reason. */}
      {view === 'keys' && (canManageKeys(session)
        ? <PlatformKeys onError={onError} openCreateNonce={createKeyNonce} />
        : <PfNotice kind="warn">
            API keys are managed by your organisation&apos;s admins and owners. Your access is
            view-only, so this screen is not available to you — ask an admin at your company if you
            need a key created, rotated or revoked.
          </PfNotice>)}
      {view === 'credits' && <PlatformCredits days={days} onError={onError} />}

      <ToastHost />
    </PlatformShell>
  )
}

// [2026-09-04] --- OLD SHELL (pre-revamp), kept per the repo rule. -------------------------------
//
// This is the 236px left sidebar this component rendered until the IncProctor revamp. It is left
// here whole rather than in git history because reverting is then a copy-paste back into the
// `return` above plus dropping the PlatformShell wrapper — no archaeology, no guessing which of the
// three shells in this repo it was.
//
// To restore: replace the `return (<PlatformShell …>` block above with this, put `RANGELESS_VIEWS`
// back to ['keys', 'requests', 'reports'], and point 'overview' at PlatformOverview again. The
// NavGroup/NavItem import at the top of this file is still present for exactly this reason, and
// SignOutIcon is still defined above.
//
//   return (
//     <div className="app">
//       <aside className="sidebar">
//         <div className="brand"><span className="brand-mark">In</span><span>InCruiter</span></div>
//
//         <NavGroup label="Dashboard">
//           <NavItem id="overview" icon="overview" view={view} setView={goTo}>Overview</NavItem>
//           <NavItem id="analytics" icon="analytics" view={view} setView={goTo}>
//             Usage &amp; analytics
//           </NavItem>
//         </NavGroup>
//
//         <NavGroup label="Reports">
//           {/* Reports first, requests second: a customer opens this product to look at CANDIDATES.
//               The raw API feed is the engineer's drill-down, and it sits below accordingly. */}
//           <NavItem id="reports" icon="candidates" view={view} setView={goTo}>
//             Proctoring reports
//           </NavItem>
//           <NavItem id="requests" icon="audit" view={view} setView={goTo}>API requests</NavItem>
//         </NavGroup>
//
//         <NavGroup label="Account">
//           <NavItem id="keys" icon="keys" view={view} setView={goTo}>API keys</NavItem>
//           <NavItem id="credits" icon="credits" view={view} setView={goTo}>Credits</NavItem>
//           {/* Shown to everyone, including viewers. Knowing who else can see your candidates'
//               recordings is part of using the product responsibly; the WRITE controls inside the
//               screen are what depend on role. */}
//           <NavItem id="members" icon="keys" view={view} setView={goTo}>Team</NavItem>
//         </NavGroup>
//
//         <div className="sidebar-foot">
//           <div style={{ padding: '0 8px 10px' }}>
//             <div className="who-email">{session?.signed_in_as?.email || session?.company}</div>
//             <div className="muted" style={{ fontSize: 11.5 }}>{session?.company || 'Client'}</div>
//           </div>
//           <button className="nav-item" onClick={signOut}><SignOutIcon />Sign out</button>
//         </div>
//       </aside>
//
//       <main className="main">
//         <div className="page-head">
//           <div>
//             <h1>{meta.title}</h1>
//             <p>{meta.blurb}</p>
//           </div>
//           {!RANGELESS_VIEWS.includes(view) && <RangePicker value={days} onChange={setDays} />}
//         </div>
//
//         {view === 'overview' && (
//           <PlatformOverview session={session} days={days} setView={goTo} onError={onError} />
//         )}
//         {view === 'analytics' && (
//           <PlatformAnalytics days={days} onError={onError} onDrillEndpoint={drillIntoEndpoint} />
//         )}
//         {view === 'reports' && <PlatformReports onError={onError} />}
//         {view === 'members' && <PlatformMembers />}
//         {view === 'requests' && (
//           <PlatformRequests key={drillEndpoint || 'all'} endpoint={drillEndpoint || undefined}
//                             onError={onError} />
//         )}
//         {view === 'keys' && <PlatformKeys onError={onError} />}
//         {view === 'credits' && <PlatformCredits days={days} onError={onError} />}
//       </main>
//
//       <ToastHost />
//     </div>
//   )
//
// --- END OLD SHELL ------------------------------------------------------------------------------


// [2026-09-06] ADDED — the two-tab host for the Proctoring Reports screen.
//
// `aria-pressed` AND NOT `role="tab"`. A button carrying role="tab" REPLACES its button role, and
// every e2e suite in this repo starts by finding controls with getByRole('button', …) — a
// segmented control written that way has already broken automated sign-in here once. That trap is
// recorded in CLAUDE.md; this follows it.
//
// The active tab is component state, deliberately not a URL parameter: `?view=reports` is a link
// already in circulation (the "check your proctoring report" email, anything bookmarked) and it
// must keep landing on the sessions tab. A tab key in the URL would be a second source of truth
// for where someone lands.
function ReportsTabs({ onError }) {
  const [tab, setTab] = useState('sessions')

  // [2026-09-07] CHANGED — named after the two PRODUCTS, not after our internals.
  //
  // OLD: 'Candidate Sessions' and 'Proctoring Processing'
  //
  // Both old labels described the mechanism: one said where the code runs, the other said what
  // happens to a recording. A customer reading them cannot tell which tab holds which half of what
  // they bought, because neither name appears on their invoice or in the SDK they integrated.
  // "SDK Sessions" and "Proctoring Sessions" are the two things they were sold, so the tabs now
  // match the vocabulary they already have.
  //
  // The ids are UNCHANGED. `sessions` and `analyses` are what the tab state and the two child
  // screens key on; renaming them would be a rename of working code to no benefit, and the id is
  // never shown to anyone.
  const TABS = [
    { id: 'sessions', label: 'SDK Sessions',
      hint: 'Sittings recorded by the browser SDK on the candidate\u2019s exam page' },
    { id: 'analyses', label: 'Proctoring Sessions',
      hint: 'Recordings submitted to the proctoring engine for analysis' },
  ]

  return (
    <>
      <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
        {TABS.map((item) => (
          <button key={item.id}
                  /* [2026-09-25] CHANGED — `pf-tab-btn` added, so the selected (blue) tab is the
                     SAME SIZE as the unselected (white) one. Asked for by Rujal: the blue one was
                     40px tall and bold, the white one 36px and regular.
                     OLD: className={`pf-btn${tab === item.id ? '' : ' ghost'}`} */
                  className={`pf-btn pf-tab-btn${tab === item.id ? '' : ' ghost'}`}
                  aria-pressed={tab === item.id}
                  title={item.hint}
                  onClick={() => setTab(item.id)}>
            {item.label}
          </button>
        ))}
      </div>

      {/* BOTH ARE MOUNTED CONDITIONALLY, so the inactive tab issues no request. Each fetches its
          own page on mount; rendering both and hiding one would double every page load of this
          screen for data most people never look at. */}
      {tab === 'sessions' && <PlatformReports onError={onError} />}
      {tab === 'analyses' && <PlatformVideoJobs onError={onError} />}
    </>
  )
}


// [2026-09-06] ADDED — the two-tab host for the API Requests screen.
//
// Written to match ReportsTabs exactly, including `aria-pressed` rather than `role="tab"` for the
// reason recorded there and in CLAUDE.md: role="tab" REPLACES the button role and breaks every e2e
// selector in this repo, which has already cost a working sign-in once.
//
// THE DRILL-IN FROM ANALYTICS LANDS ON THE ALL APIs TAB, which is where the endpoint filter lives.
// That is not enforced here — it falls out of the shell remounting this component whenever the
// drill target changes (PlatformApp keys it on `endpoint`), which resets `tab` to its initial
// value along with the table's seeded filter. Both pieces of state are restored by the one remount,
// so a drill can never land on a tab that ignores the endpoint the user just clicked.
function ApiRequestTabs({ onError, endpoint }) {
  const [tab, setTab] = useState('sdk')

  // [2026-09-07] CHANGED — split by PRODUCT, which is how the customer buys it.
  //
  // OLD: 'All APIs' and 'Video Processing'.
  //
  // Both tabs now filter server-side on the endpoint prefix (GET /me/requests?service=), rather
  // than one showing everything. Labelling the old 'All APIs' tab "SDK Requests" without that
  // filter would have been a lie the customer only discovers while reconciling.
  const TABS = [
    { id: 'sdk', label: 'SDK Requests',
      hint: 'Calls the browser SDK made from the candidate\u2019s exam page' },
    { id: 'proctoring', label: 'Proctoring Requests',
      hint: 'Calls to the proctoring API, accepted and refused' },
  ]

  return (
    <>
      <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
        {TABS.map((item) => (
          <button key={item.id}
                  /* [2026-09-25] CHANGED — `pf-tab-btn` added, so the selected (blue) tab is the
                     SAME SIZE as the unselected (white) one. Asked for by Rujal: the blue one was
                     40px tall and bold, the white one 36px and regular.
                     OLD: className={`pf-btn${tab === item.id ? '' : ' ghost'}`} */
                  className={`pf-btn pf-tab-btn${tab === item.id ? '' : ' ghost'}`}
                  aria-pressed={tab === item.id}
                  title={item.hint}
                  onClick={() => setTab(item.id)}>
            {item.label}
          </button>
        ))}
      </div>

      {/* Conditional, not hidden: the inactive tab issues no request. Both read the same TTL
          collection through GET /me/requests, so mounting both would double every page load. */}
      {tab === 'sdk' && (
        <PlatformRequests key={endpoint || 'sdk'} endpoint={endpoint} service="sdk"
                          onError={onError} />
      )}
      {/* [2026-09-07] The proctoring tab keeps its PURPOSE-BUILT screen rather than the generic
          feed filtered to service=proctoring. PlatformVideoRequests carries a Job ID column and
          says "Accepted / Refused / Engine error" instead of "Success / Client error / Server
          error" — for a queue-and-callback API the job id is the thing support asks for, and
          "client error" is a poor name for "we refused it". Filtering the generic table here would
          have looked like a tidy simplification and quietly dropped both. */}
      {tab === 'proctoring' && <PlatformVideoRequests onError={onError} />}
    </>
  )
}
